"use client"

import { useEffect, useState } from "react"
import { cn } from "@/lib/utils"
import Link from "next/link"

interface BlogLayoutTitleProps {
  /** The title of the current blog post */
  postTitle?: string
  /** The name of the blog */
  blogName?: string

  /**
   * The href to navigate to when the blog name is clicked
   */
  href?: string
}

interface TitleTextProps {
  /** The text content to display */
  children: string
  /** Additional classes to apply to the title */
  className?: string
  /** Whether the text should be invisible (for spacer) */
  invisible?: boolean

  href?: string
}

const SCROLL_THRESHOLD_PX = 520

/**
 * Shared title component with consistent styling
 * Renders as a Next.js Link when href is provided, otherwise as a plain h1
 */
const TitleText = ({
  children,
  className,
  invisible = false,
  href,
}: TitleTextProps) => {
  const sharedClassName = cn(
    "xl:text-center text-base md:text-lg xl:text-xl font-header transition-all duration-300 block xl:mx-auto h-fit truncate my-auto xl:max-w-[876px]",
    invisible && "sm:invisible hidden",
    !invisible && "xl:absolute inset-x-0",
    className
  )

  if (href) {
    return (
      <h1 className={sharedClassName}>
        <Link href={href}>{children}</Link>
      </h1>
    )
  }

  return <h1 className={sharedClassName}>{children}</h1>
}

/**
 * A dynamic blog header that transitions between blog name and post title on scroll
 */
export default function BlogLayoutTitle({
  postTitle,
  blogName,
  href,
}: BlogLayoutTitleProps) {
  const [isScrolled, setIsScrolled] = useState(false)

  useEffect(() => {
    if (!postTitle) return

    const handleScroll = () => {
      setIsScrolled(window.scrollY > SCROLL_THRESHOLD_PX)
    }

    window.addEventListener("scroll", handleScroll, { passive: true })
    return () => window.removeEventListener("scroll", handleScroll)
  }, [postTitle])

  const displayText = postTitle || blogName || ""

  return (
    <div className="items-center flex-1 hidden min-w-0 h-14 sm:flex lg:justify-center">
      {/* Invisible spacer to maintain container size */}
      <TitleText invisible>{displayText}</TitleText>

      {blogName && (
        <TitleText
          href={href}
          className={
            isScrolled && postTitle
              ? "opacity-0 -translate-y-2 z-0 w-0"
              : "opacity-100 translate-y-0 z-10"
          }
        >
          {blogName}
        </TitleText>
      )}

      {postTitle && (
        <TitleText
          className={
            isScrolled
              ? "opacity-100 translate-y-0 z-10 xl:pr-[85px]"
              : "opacity-0 translate-y-2 z-0 w-0"
          }
        >
          {postTitle}
        </TitleText>
      )}
    </div>
  )
}
